import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import { Wrench, Package, CheckCircle, XCircle, LayoutGrid, List } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { sparePartPromotions, type SparePartPromotion } from "@/data/promotions-spare-parts";
import { equipmentPromotions, type EquipmentPromotion } from "@/data/promotions-equipment";
import { Badge } from "@/components/ui/badge";
import indisponibilOverlay from "@/assets/indisponibil-overlay.png";

type TabType = "equipment" | "spareParts";
type ViewMode = "grid" | "list";

function isExpired(validUntil?: string): boolean {
  if (!validUntil) return false;
  return new Date(validUntil) < new Date();
}

export default function Promotions() {
  const { t, lang: language } = useLanguage();
  const [activeTab, setActiveTab] = useState<TabType>("equipment");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  return (
    <div className="py-16 lg:py-24">
      <SEOHead
        title="Promoții | WITTMANN ROMANIA"
        description="Oferte speciale la piese de schimb și echipamente WITTMANN. Promoții pentru industria maselor plastice."
        path="/promotii"
      />
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10"
        >
          <h1 className="text-3xl lg:text-5xl font-bold text-foreground mb-3">
            {t("promotions.title")}
          </h1>
          <p className="text-lg text-muted-foreground">
            {t("promotions.subtitle")}
          </p>
        </motion.div>

        {/* Toolbar: Tabs + View Toggle */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-4 border-b border-border">
          {/* Tabs */}
          <div className="flex">
            <button
              onClick={() => setActiveTab("equipment")}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "equipment"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Package className="w-4 h-4" />
              {t("promotions.equipment")}
            </button>
            <button
              onClick={() => setActiveTab("spareParts")}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === "spareParts"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Wrench className="w-4 h-4" />
              {t("promotions.spareParts")}
            </button>
          </div>

          {/* View toggle */}
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-md transition-colors ${viewMode === "grid" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              title="Grid"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-md transition-colors ${viewMode === "list" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              title="List"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "equipment" ? (
          <EquipmentTab language={language} t={t} viewMode={viewMode} />
        ) : (
          <SparePartsTab language={language} t={t} viewMode={viewMode} />
        )}
      </div>
    </div>
  );
}

/* ── Equipment ─────────────────────────────────────────── */

function EquipmentCardGrid({ item, language }: { item: EquipmentPromotion; language: string }) {
  const available = item.inStock && !isExpired(item.validUntil);
  return (
    <div className="group bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1 flex flex-col">
      <div className="relative aspect-[4/3] bg-muted overflow-hidden">
        <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        {!available && <UnavailableOverlay />}
        <Badge className={`absolute top-3 left-3 z-20 ${item.condition === "new" ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"}`}>
          {item.condition === "new" ? (language === "ro" ? "Nou" : "New") : (language === "ro" ? "Second-hand" : "Used")}
        </Badge>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-lg font-bold text-foreground leading-tight">{item.name}</h3>
          <StockBadge inStock={available} language={language} labelIn={language === "ro" ? "Disponibil" : "Available"} labelOut={language === "ro" ? "Indisponibil" : "Unavailable"} />
        </div>
        {item.specs && <p className="text-xs text-muted-foreground mb-2 font-mono">{item.specs}</p>}
        <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1" dangerouslySetInnerHTML={{ __html: item.description[language] }} />
        <PriceBlock newPrice={item.newPrice} oldPrice={item.oldPrice} currency={item.currency} />
        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
          {item.year && <span>{language === "ro" ? "An fabricație" : "Year"}: {item.year}</span>}
          {item.validUntil && <span>{language === "ro" ? "Valabil până la" : "Valid until"}: {item.validUntil}</span>}
        </div>
      </div>
    </div>
  );
}

function EquipmentCardList({ item, language }: { item: EquipmentPromotion; language: string }) {
  const available = item.inStock && !isExpired(item.validUntil);
  return (
    <div className="group flex flex-row bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all">
      <div className="relative w-[30%] sm:w-64 shrink-0 aspect-square sm:aspect-auto sm:h-48 overflow-hidden bg-muted rounded-l-xl">
        <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        {!available && <UnavailableOverlay />}
        <Badge className={`absolute top-3 left-3 z-20 ${item.condition === "new" ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"}`}>
          {item.condition === "new" ? (language === "ro" ? "Nou" : "New") : (language === "ro" ? "Second-hand" : "Used")}
        </Badge>
      </div>
      <div className="flex-1 p-5 flex flex-col justify-center">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">{item.name}</h3>
          <StockBadge inStock={available} language={language} labelIn={language === "ro" ? "Disponibil" : "Available"} labelOut={language === "ro" ? "Indisponibil" : "Unavailable"} />
        </div>
        {item.specs && <p className="text-xs text-muted-foreground mb-1 font-mono">{item.specs}</p>}
        <p className="text-sm text-muted-foreground mb-3" dangerouslySetInnerHTML={{ __html: item.description[language] }} />
        <PriceBlock newPrice={item.newPrice} oldPrice={item.oldPrice} currency={item.currency} />
        
        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
          {item.year && <span>{language === "ro" ? "An fabricație" : "Year"}: {item.year}</span>}
          {item.validUntil && <span>{language === "ro" ? "Valabil până la" : "Valid until"}: {item.validUntil}</span>}
        </div>
      </div>
    </div>
  );
}

function EquipmentTab({ language, t, viewMode }: { language: string; t: (key: string) => string; viewMode: ViewMode }) {
  if (equipmentPromotions.length === 0) {
    return <p className="text-muted-foreground italic text-sm">{t("promotions.comingSoon")}</p>;
  }

  if (viewMode === "list") {
    return (
      <div className="flex flex-col gap-4">
        {equipmentPromotions.map((item, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}>
            <EquipmentCardList item={item} language={language} />
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {equipmentPromotions.map((item, i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}>
          <EquipmentCardGrid item={item} language={language} />
        </motion.div>
      ))}
    </div>
  );
}

/* ── Spare Parts ───────────────────────────────────────── */

function SparePartCardGrid({ item, language }: { item: SparePartPromotion; language: string }) {
  const available = item.inStock && !isExpired(item.validUntil);
  return (
    <div className="group bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1 flex flex-col">
      <div className="relative aspect-[4/3] bg-muted overflow-hidden">
        <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        {!available && <UnavailableOverlay />}
        <Badge className={`absolute top-3 left-3 z-20 ${item.condition === "new" ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"}`}>
          {item.condition === "new" ? (language === "ro" ? "Nou" : "New") : (language === "ro" ? "Second-hand" : "Used")}
        </Badge>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-lg font-bold text-foreground leading-tight">{item.name}</h3>
          <StockBadge inStock={available} language={language} labelIn={language === "ro" ? "În stoc" : "In stock"} labelOut={language === "ro" ? "Indisponibil" : "Out of stock"} />
        </div>
        {item.partNumber && <p className="text-xs text-muted-foreground mb-2 font-mono">{item.partNumber}</p>}
        <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1" dangerouslySetInnerHTML={{ __html: item.description[language] }} />
        <PriceBlock newPrice={item.newPrice} oldPrice={item.oldPrice} currency={item.currency} />
        {item.validUntil && (
          <p className="text-xs text-muted-foreground mt-2">{language === "ro" ? "Valabil până la" : "Valid until"}: {item.validUntil}</p>
        )}
      </div>
    </div>
  );
}

function SparePartCardList({ item, language }: { item: SparePartPromotion; language: string }) {
  const available = item.inStock && !isExpired(item.validUntil);
  return (
    <div className="group flex flex-row bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all">
      <div className="relative w-[30%] sm:w-64 shrink-0 aspect-square sm:aspect-auto sm:h-48 overflow-hidden bg-muted rounded-l-xl">
        <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        {!available && <UnavailableOverlay />}
        <Badge className={`absolute top-3 left-3 z-20 ${item.condition === "new" ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground"}`}>
          {item.condition === "new" ? (language === "ro" ? "Nou" : "New") : (language === "ro" ? "Second-hand" : "Used")}
        </Badge>
      </div>
      <div className="flex-1 p-5 flex flex-col justify-center">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">{item.name}</h3>
          <StockBadge inStock={available} language={language} labelIn={language === "ro" ? "În stoc" : "In stock"} labelOut={language === "ro" ? "Indisponibil" : "Out of stock"} />
        </div>
        {item.partNumber && <p className="text-xs text-muted-foreground mb-1 font-mono">{item.partNumber}</p>}
        <p className="text-sm text-muted-foreground mb-3" dangerouslySetInnerHTML={{ __html: item.description[language] }} />
        <PriceBlock newPrice={item.newPrice} oldPrice={item.oldPrice} currency={item.currency} />
        {item.validUntil && (
          <p className="text-xs text-muted-foreground mt-2">{language === "ro" ? "Valabil până la" : "Valid until"}: {item.validUntil}</p>
        )}
      </div>
    </div>
  );
}

function SparePartsTab({ language, t, viewMode }: { language: string; t: (key: string) => string; viewMode: ViewMode }) {
  if (sparePartPromotions.length === 0) {
    return <p className="text-muted-foreground italic text-sm">{t("promotions.comingSoon")}</p>;
  }

  if (viewMode === "list") {
    return (
      <div className="flex flex-col gap-4">
        {sparePartPromotions.map((item, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}>
            <SparePartCardList item={item} language={language} />
          </motion.div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {sparePartPromotions.map((item, i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}>
          <SparePartCardGrid item={item} language={language} />
        </motion.div>
      ))}
    </div>
  );
}

/* ── Shared UI ─────────────────────────────────────────── */

function UnavailableOverlay() {
  return (
    <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
      <img
        src={indisponibilOverlay}
        alt="Indisponibil"
        className="w-[80%] max-w-[280px] select-none pointer-events-none drop-shadow-lg"
        draggable={false}
      />
    </div>
  );
}

function StockBadge({ inStock, language, labelIn, labelOut }: { inStock: boolean; language: string; labelIn: string; labelOut: string }) {
  return inStock ? (
    <Badge variant="outline" className="text-green-600 border-green-300 shrink-0 text-xs">
      <CheckCircle className="w-3 h-3 mr-1" />
      {labelIn}
    </Badge>
  ) : (
    <Badge variant="outline" className="text-destructive border-destructive/30 shrink-0 text-xs">
      <XCircle className="w-3 h-3 mr-1" />
      {labelOut}
    </Badge>
  );
}

function PriceBlock({ newPrice, oldPrice, currency }: { newPrice: number; oldPrice?: number; currency: string }) {
  return (
    <div className="flex items-baseline gap-2 mt-auto">
      <div className="flex flex-col">
        <span className="text-xl font-bold text-primary">{newPrice.toLocaleString("de-DE")} {currency}</span>
        <span className="text-xs font-semibold text-primary">DAP Client</span>
      </div>
      {oldPrice && (
        <span className="text-sm text-muted-foreground line-through">{oldPrice.toLocaleString("de-DE")} {currency}</span>
      )}
    </div>
  );
}
